import { AlertTriangle, Button } from "@ews-admin/global-design-system";
import { Select } from "../../components/atoms/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Modal } from "../../components/molecules/Modal/Modal";
import {
  GRANT_FREE_ACCESS_DURATIONS,
  GrantFreeAccessFormData,
  grantFreeAccessSchema,
} from "../../schemas/grantFreeAccess.schema";
import {
  AdminDoctorLicence,
  GrantFreeAccessPayload,
} from "../../types/doctorAdmin.definition";
import { LicenceStatus } from "../../types/enums";
import { formatDateTime } from "../../utils/date-format-utils";

interface GrantFreeAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: GrantFreeAccessPayload) => void;
  /** The doctor's current licence, if any — drives "grant" vs "extend" copy. */
  currentLicence?: AdminDoctorLicence | null;
  isSaving: boolean;
  /**
   * Inline error to surface above the form actions (e.g. the
   * FREE_ACCESS_BLOCKED_ACTIVE_PAID_LICENCE case). The parent owns the
   * message and clears it when desired.
   */
  inlineError?: string | null;
}

export const GrantFreeAccessModal: React.FC<GrantFreeAccessModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  currentLicence,
  isSaving,
  inlineError,
}) => {
  const { t } = useTranslation();
  const prevIsOpen = useRef(false);

  const isExtend = currentLicence?.status === LicenceStatus.TRIAL;
  // Mirrors the API's block predicate in DoctorLicenceServiceImpl#grantFreeAccess:
  // anything other than no-licence/EXPIRED (fresh grant) or TRIAL (extend) is blocked.
  const hasActivePaidLicence =
    !!currentLicence?.status &&
    currentLicence.status !== LicenceStatus.EXPIRED &&
    currentLicence.status !== LicenceStatus.TRIAL;

  const {
    handleSubmit,
    control,
    register,
    reset,
    formState: { errors, isValid },
  } = useForm<GrantFreeAccessFormData>({
    resolver: zodResolver(grantFreeAccessSchema(t)),
    mode: "onChange",
    defaultValues: {
      durationMonths: GRANT_FREE_ACCESS_DURATIONS[0],
      reason: "",
    },
  });

  // Reset form only when the modal opens (false -> true). Avoid resetting on
  // every render so the user's input survives re-renders.
  useEffect(() => {
    if (!isOpen || prevIsOpen.current === isOpen) {
      if (!isOpen) prevIsOpen.current = false;
      return;
    }
    prevIsOpen.current = isOpen;

    reset({
      durationMonths: GRANT_FREE_ACCESS_DURATIONS[0],
      reason: "",
    });
  }, [isOpen, reset]);

  const handleFormSubmit = (data: GrantFreeAccessFormData) => {
    onSubmit({
      durationMonths: Number(data.durationMonths),
      reason: data.reason.trim(),
    });
  };

  const durationOptions = GRANT_FREE_ACCESS_DURATIONS.map(value => ({
    value,
    label: t("admin.doctors.grant.form.durationOption", {
      count: Number(value),
    }),
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !isSaving && onClose()}
      title={
        isExtend
          ? t("admin.doctors.grant.extendTitle")
          : t("admin.doctors.grant.title")
      }
      size="sm"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="p-6 space-y-4">
          <p className="text-sm text-neutral-600">
            {isExtend
              ? t("admin.doctors.grant.extendDescription")
              : t("admin.doctors.grant.description")}
          </p>

          {isExtend && currentLicence?.endDate && (
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
              {t("admin.doctors.grant.currentGrant", {
                endDate: formatDateTime(currentLicence.endDate),
              })}
            </div>
          )}

          {hasActivePaidLicence && (
            <div className="flex items-start gap-2 rounded-md bg-warning-50 border border-warning-200 p-3 text-sm text-warning-800">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{t("admin.doctors.grant.paidLicenceWarning")}</span>
            </div>
          )}

          {inlineError && (
            <div className="rounded-md bg-danger-50 border border-danger-200 p-3 text-sm text-danger-700">
              {inlineError}
            </div>
          )}

          <Controller
            name="durationMonths"
            control={control}
            render={({ field }) => (
              <Select
                label={t("admin.doctors.grant.form.duration")}
                options={durationOptions}
                value={field.value}
                onChange={value => field.onChange(value as string)}
                error={errors.durationMonths?.message}
              />
            )}
          />

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t("admin.doctors.grant.form.reason")}
            </label>
            <textarea
              className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ews-primary resize-none"
              rows={3}
              placeholder={t("admin.doctors.grant.form.reasonPlaceholder")}
              {...register("reason")}
            />
            {errors.reason?.message && (
              <p className="mt-1 text-sm text-danger-600">
                {errors.reason.message}
              </p>
            )}
          </div>

          <p className="text-xs text-neutral-500">
            {t("admin.doctors.grant.form.startNote")}
          </p>
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end space-x-3">
          <Button
            variant="outline"
            type="button"
            onClick={onClose}
            disabled={isSaving}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            loading={isSaving}
            disabled={isSaving || !isValid}
          >
            {isExtend
              ? t("admin.doctors.grant.extendSubmit")
              : t("admin.doctors.grant.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
